import type { ReactNode } from 'react';
import './ListPane.css';

/**
 * ListPane — C형 화면의 **좌측 데이터 목록 패널** (디자인 정본 A8 · P1 C형 · F16).
 *
 * A2 `SecondaryPane`(240 · 메뉴 트리)와 **다른 물건이다.** 240 은 「어디로 갈지」를 고르는
 * 라우트 트리이고, 여기 280(`--layout-list-w`)은 「무엇을 볼지」를 고르는 **데이터 목록**이다.
 * 정본 P1 이 「A 와 C 를 섞지 않는다」고 못 박은 그 C 쪽이다.
 *
 * 층은 다섯이고 위에서 아래로 이렇다 — 가운데(목록)만 스크롤한다.
 *   ① 머리   제목 + 개수
 *   ② 동작   `action` — 「글쓰기」처럼 **매일 하는 것**(정본 P3-4)
 *   ③ 찾기   `find`   — 정본 A2 규칙대로 **항목 20+ 일 때만** 준다
 *   ④ 목록   `children` — `ListPaneGroup` · `ListPaneItem`
 *   ⑤ 발     `footer` — 「휴지통」처럼 하루에 몇 번 안 쓰는 것
 *
 * ★★★ **접히지 않는다** (2026-09-08 사용자 지시 — 워크스페이스 · 게시판 둘 다 삭제).
 *    2026-09-07 에는 「상시 5분의 1 점유」의 답으로 접기를 붙였는데, 써 보니 **패널이 사라지는
 *    상태가 하나 더 생기는 것**이 더 큰 값이었다. 좌측은 늘 서 있다 — A 의 트리와 같다.
 *    ★ 걷어낸 것: 머리의 접기 단추 · 펴는 단추(`ListPaneToggle`) · 기기에 남기던
 *      접힘 상태(`useListPaneFold` · `localStorage['listpane.folded.*']`) · 좁은 폭 기본 접힘.
 */

interface ListPaneBaseProps {
  /** 머리에 서는 이름 — 「게시판」 · 「대화방」. */
  title: string;
  /** 제목 옆 개수. 0 도 그린다(「없다」와 「모른다」는 다르다). */
  count?: number;
  /** `<nav>` 가 읽어 주는 이름. */
  ariaLabel: string;
  /** 머리 아래 주 동작 — 매일 하는 것 하나. 둘 이상 두지 않는다. */
  action?: ReactNode;
  /** 항목 찾기 칸. **항목 20+ 일 때만** 준다(A2). */
  find?: ReactNode;
  /** 발 — 조용한 자리. */
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}

export type ListPaneProps = ListPaneBaseProps;

export function ListPane({
  title, count, ariaLabel, action, find, footer, children, className,
}: ListPaneProps) {
  return (
    <aside className={['wc-listpane', className ?? ''].filter(Boolean).join(' ')}>
      <div className="wc-listpane__head">
        <h2 className="wc-listpane__title">{title}</h2>
        {count != null && <span className="wc-listpane__count">{count}</span>}
      </div>

      {action && <div className="wc-listpane__action">{action}</div>}
      {find && <div className="wc-listpane__find">{find}</div>}

      <nav className="wc-listpane__nav" aria-label={ariaLabel}>{children}</nav>

      {footer && <div className="wc-listpane__foot">{footer}</div>}
    </aside>
  );
}

/** 구획 — 「전사 · 부서 · 팀 · 내 게시판」처럼 목록을 가르는 소제목. */
export interface ListPaneGroupProps {
  label: string;
  icon?: ReactNode;
  /** 구획 안 항목 수. */
  count?: number;
  children: ReactNode;
}

export function ListPaneGroup({ label, icon, count, children }: ListPaneGroupProps) {
  return (
    <section className="wc-listpane__group">
      <h3 className="wc-listpane__label">
        {icon}
        {label}
        {count != null && <span className="wc-listpane__gcount">{count}</span>}
      </h3>
      {children}
    </section>
  );
}

/**
 * 목록 한 줄 — 아이콘 + 이름 + 배지, 끝에 줄 작업.
 *
 * ★ 높이는 32(`--h-md`)다. A2 의 28 보다 한 단 높은 이유는 **배지와 줄 작업이 함께 서기**
 *   때문이고, E2 `ListRow` 의 48 보다 낮은 이유는 **아바타도 두 줄도 없기** 때문이다.
 * ★★ 줄 작업(`actions`)은 손이 닿거나 **키보드 포커스가 들어올 때** 보인다(E1 과 같은 계약).
 *   늘 보여야 하는 작업은 여기 두지 않는다 — 그건 발(`footer`)이나 화면 머리의 일이다.
 */
export interface ListPaneItemProps {
  name: string;
  icon?: ReactNode;
  /** 이름 뒤 표식 — 안 읽음 개수 등. */
  badge?: ReactNode;
  selected?: boolean;
  onClick: () => void;
  /** 줄 끝 — hover · 포커스에만 보인다. */
  actions?: ReactNode;
}

export function ListPaneItem({ name, icon, badge, selected = false, onClick, actions }: ListPaneItemProps) {
  return (
    <div className={`wc-listpane__row${selected ? ' is-selected' : ''}`}>
      <button
        type="button"
        className="wc-listpane__item"
        aria-current={selected || undefined}
        onClick={onClick}
      >
        {icon}
        <span className="wc-listpane__name" title={name}>{name}</span>
        {badge != null && <span className="wc-listpane__badge">{badge}</span>}
      </button>
      {actions && <span className="wc-listpane__actions">{actions}</span>}
    </div>
  );
}

/** 목록이 비었을 때 그 자리에 놓는 한 줄. */
export function ListPaneEmpty({ children }: { children: ReactNode }) {
  return <p className="wc-listpane__empty">{children}</p>;
}
